/**
 * The editable configuration.
 *
 * Held as a scenario plus a set of overrides rather than as a flattened config, for two
 * reasons that both matter later: the overrides are exactly what a share link needs to
 * carry (a scenario id and the handful of things the sender changed, not four hundred
 * numbers), and keeping the original scenario intact means "reset this back to the
 * scenario's value" is always available for any single parameter.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  DEFAULT_TUNABLES,
  scenarioToConfig,
  writeParameter,
  type ParameterMeta,
  type Scenario,
  type ScenarioOverrides,
  type SimulationConfig,
  type Tunables,
} from 'walkout-engine';
import { DEFAULT_SCENARIO } from './scenarios.ts';

export interface ConfigState {
  scenario: Scenario;
  overrides: ScenarioOverrides;
  config: SimulationConfig;
  /** How many parameters the user has moved away from the scenario's own values. */
  editCount: number;
  selectScenario: (scenario: Scenario) => void;
  setSeed: (seed: number) => void;
  setHorizon: (days: number) => void;
  setSectorParameter: (sectorId: string, meta: ParameterMeta, value: number) => void;
  setPillarParameter: (pillarId: string, meta: ParameterMeta, value: number) => void;
  setGroupParameter: (
    group: 'movement' | 'media' | 'regime',
    meta: ParameterMeta,
    value: number,
  ) => void;
  setTunable: (key: keyof Tunables, value: number) => void;
  resetEdits: () => void;
  replaceOverrides: (overrides: ScenarioOverrides) => void;
}

function countEdits(overrides: ScenarioOverrides): number {
  let count = 0;
  for (const patch of Object.values(overrides.sectors ?? {})) count += Object.keys(patch).length;
  for (const patch of Object.values(overrides.pillars ?? {})) count += Object.keys(patch).length;
  count += Object.keys(overrides.movement ?? {}).length;
  count += Object.keys(overrides.media ?? {}).length;
  count += Object.keys(overrides.regime ?? {}).length;
  count += Object.keys(overrides.tunables ?? {}).length;
  count += overrides.addSectors?.length ?? 0;
  count += overrides.addPillars?.length ?? 0;
  count += overrides.removeSectors?.length ?? 0;
  count += overrides.removePillars?.length ?? 0;
  return count;
}

export function useConfig(initialScenario: Scenario = DEFAULT_SCENARIO): ConfigState {
  const [scenario, setScenario] = useState<Scenario>(initialScenario);
  const [overrides, setOverrides] = useState<ScenarioOverrides>({ seed: 1 });

  const config = useMemo(() => scenarioToConfig(scenario, overrides), [scenario, overrides]);

  const selectScenario = useCallback((next: Scenario) => {
    setScenario(next);
    // Overrides are keyed by sector and pillar id, and the ids differ between scenarios.
    // Carrying them across would silently apply a buffer figure meant for dockers to
    // whichever sector happened to share its id. Keep the seed; drop the rest.
    setOverrides((current) => ({ seed: current.seed }));
  }, []);

  /**
   * Merge one parameter into an entity's patch.
   *
   * The current value is read from the *config* rather than from the scenario, so nested
   * paths like `threshold.mean` keep any sibling edit already made to `threshold.sd`.
   */
  const patchEntity = useCallback(
    (kind: 'sectors' | 'pillars', id: string, meta: ParameterMeta, value: number) => {
      setOverrides((current) => {
        const collection = current[kind] ?? {};
        const existing = (collection as Record<string, Record<string, unknown>>)[id] ?? {};
        const source = kind === 'sectors'
          ? scenario.sectors.find((s) => s.id === id)
          : scenario.pillars.find((p) => p.id === id);
        const merged = writeParameter({ ...source, ...existing }, meta.path, value, meta) as Record<
          string,
          unknown
        >;

        // Keep only the fields the user has actually touched, so the override set stays a
        // record of intent rather than a full copy of the scenario.
        const touched = new Set([...Object.keys(existing), meta.path.split('.')[0] as string]);
        const patch: Record<string, unknown> = {};
        for (const key of touched) patch[key] = merged[key];

        return { ...current, [kind]: { ...collection, [id]: patch } };
      });
    },
    [scenario],
  );

  const setGroupParameter = useCallback(
    (group: 'movement' | 'media' | 'regime', meta: ParameterMeta, value: number) => {
      setOverrides((current) => ({
        ...current,
        [group]: writeParameter({ ...current[group] }, meta.path, value, meta),
      }));
    },
    [],
  );

  return {
    scenario,
    overrides,
    config,
    editCount: countEdits(overrides),
    selectScenario,
    setSeed: (seed) => setOverrides((current) => ({ ...current, seed })),
    setHorizon: (horizonDays) => setOverrides((current) => ({ ...current, horizonDays })),
    setSectorParameter: (sectorId, meta, value) => patchEntity('sectors', sectorId, meta, value),
    setPillarParameter: (pillarId, meta, value) => patchEntity('pillars', pillarId, meta, value),
    setGroupParameter,
    setTunable: (key, value) =>
      setOverrides((current) => ({
        ...current,
        tunables: { ...DEFAULT_TUNABLES, ...current.tunables, [key]: value },
      })),
    resetEdits: () => setOverrides((current) => ({ seed: current.seed })),
    replaceOverrides: setOverrides,
  };
}
